import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, X } from "lucide-react";
import { useCurrency } from "../../lib/currency";
import ModalPortal from "../../components/ModalPortal";

const EMPTY = {
  sku: "", category: "website", name: "", price: 0, billing_cycle: "one_time",
  description: "", features: "", popular: false,
};

const CATEGORIES = ["website", "seo", "api", "automation", "hosting", "ecommerce"];
const CYCLES = ["one_time", "monthly", "yearly"];

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all");
  const { format } = useCurrency();

  const load = async () => {
    setLoading(true);
    try {
      const calls = filter === "all"
        ? CATEGORIES.map(c => api.get(`/products?category=${c}`).then(r=>r.data))
        : [api.get(`/products?category=${filter}`).then(r=>r.data)];
      const results = await Promise.all(calls);
      setItems(results.flat());
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const openNew = () => { setForm(EMPTY); setEditing("new"); };
  const openEdit = (p) => {
    setForm({
      sku: p.sku, category: p.category, name: p.name, price: p.price,
      billing_cycle: p.billing_cycle, description: p.description || "",
      features: (p.features || []).join(", "), popular: !!p.popular,
    });
    setEditing(p.sku);
  };
  const close = () => { setEditing(null); setForm(EMPTY); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      const payload = {
        ...form,
        price: Number(form.price),
        features: form.features.split(",").map(f=>f.trim()).filter(Boolean),
      };
      if (editing === "new") await api.post("/products", payload);
      else toast.info("Edit endpoint coming soon — delete + recreate for now.");
      toast.success("Saved");
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const del = async (sku) => {
    if (!window.confirm(`Deactivate product ${sku}?`)) return;
    try { await api.delete(`/products/${sku}`); toast.success("Deactivated"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-white" style={{fontFamily:'Outfit'}}>Products</h2>
          <p className="text-slate-400 text-sm">{items.length} active plans · 6 categories</p>
        </div>
        <div className="flex items-center gap-2">
          <select value={filter} onChange={e=>setFilter(e.target.value)} className="input !py-2 text-sm">
            <option value="all">All categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <button onClick={openNew} className="btn-primary !py-2 !px-4 text-sm" data-testid="product-new"><Plus size={14}/> New</button>
        </div>
      </div>

      {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-blue-400"/></div> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-wider text-slate-500 border-b border-slate-700">
              <tr><th className="py-3 px-4 text-left">SKU</th><th className="text-left">Name</th><th className="text-left">Category</th><th className="text-right">Price</th><th className="text-left">Cycle</th><th className="text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map(p => (
                <tr key={p.sku} className="border-b border-slate-800">
                  <td className="py-3 px-4 text-cyan-300 font-mono text-xs">{p.sku}</td>
                  <td className="text-white">{p.name} {p.popular && <span className="ml-1 text-[10px] text-amber-400">★</span>}</td>
                  <td className="text-slate-300">{p.category}</td>
                  <td className="text-right text-white">{format(p.price)}</td>
                  <td className="text-slate-400">{p.billing_cycle}</td>
                  <td className="text-right space-x-2 pr-4">
                    <button onClick={()=>openEdit(p)} className="text-slate-400 hover:text-blue-400" title="Edit"><Pencil size={15}/></button>
                    <button onClick={()=>del(p.sku)} className="text-slate-400 hover:text-rose-400" title="Deactivate"><Trash2 size={15}/></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <ModalPortal><div className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center px-5 py-10 overflow-auto" onClick={close}>
          <form onSubmit={save} onClick={e=>e.stopPropagation()} className="card p-6 w-full max-w-2xl space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{editing === "new" ? "New Product" : `Edit ${editing}`}</h3>
              <button type="button" onClick={close} className="text-slate-400 hover:text-white"><X size={20}/></button>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="label">SKU</label><input required value={form.sku} onChange={e=>setForm({...form,sku:e.target.value})} className="input"/></div>
              <div><label className="label">Category</label>
                <select value={form.category} onChange={e=>setForm({...form,category:e.target.value})} className="input">
                  {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div><label className="label">Name</label><input required value={form.name} onChange={e=>setForm({...form,name:e.target.value})} className="input"/></div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div><label className="label">Price (INR)</label><input type="number" required value={form.price} onChange={e=>setForm({...form,price:e.target.value})} className="input"/></div>
              <div><label className="label">Billing Cycle</label>
                <select value={form.billing_cycle} onChange={e=>setForm({...form,billing_cycle:e.target.value})} className="input">
                  {CYCLES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div><label className="label">Description</label><textarea value={form.description} onChange={e=>setForm({...form,description:e.target.value})} className="input" rows={2}/></div>
            <div><label className="label">Features (comma-separated)</label><input value={form.features} onChange={e=>setForm({...form,features:e.target.value})} className="input"/></div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
              <input type="checkbox" checked={form.popular} onChange={e=>setForm({...form,popular:e.target.checked})} className="w-4 h-4 accent-blue-500"/>
              Mark as Most Popular
            </label>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={close} className="btn-ghost">Cancel</button>
              <button type="submit" disabled={saving} className="btn-primary">{saving ? <Loader2 size={14} className="animate-spin"/> : "Save"}</button>
            </div>
          </form>
        </div></ModalPortal>
      )}
    </div>
  );
}
